import React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import { Accordion } from '@highereducation/hen-ui';

type AccordionShortcodeProps = {
  variant?: 'no-background' | 'white' | 'neutral' | 'primary' | 'secondary';
  children?: React.ReactNode;
  [key: string]: unknown;
};

type AccordionItemShortcodeProps = {
  title?: string;
  expanded?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * AccordionShortcode - Shortcode wrapper for Accordion composite component.
 * Use [hen-ui-accordion-item] as children for each collapsible section.
 *
 * @example
 * [hen-ui-accordion variant="white"]
 *   [hen-ui-accordion-item title="Question one?" expanded="true"]Answer one.[/hen-ui-accordion-item]
 *   [hen-ui-accordion-item title="Question two?"]Answer two.[/hen-ui-accordion-item]
 * [/hen-ui-accordion]
 */
export const AccordionShortcode: ShortCode<AccordionShortcodeProps> = {
  component: ({ variant = 'no-background', children }) => {
    const validChildren = React.Children.toArray(children).filter(
      (child): child is React.ReactElement<AccordionItemShortcodeProps> =>
        React.isValidElement(child),
    );

    const items = validChildren.map((child) => ({
      title: child.props.title ?? '',
      content: child.props.children,
    }));

    if (items.length === 0) return null;

    const defaultExpandedIndex = validChildren.findIndex(
      (child) => child.props.expanded === 'true',
    );
    const defaultValue =
      defaultExpandedIndex >= 0 ? `item-${defaultExpandedIndex}` : undefined;

    return (
      <Accordion
        variant={variant}
        type='single'
        collapsible
        items={items}
        defaultValue={defaultValue}
      />
    );
  },
};

export const AccordionItemShortcode: ShortCode<AccordionItemShortcodeProps> = {
  component: () => null,
};
